import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  Flex,
  Image,
} from "@chakra-ui/react";
import { Fatura,  Pix } from "../../utils/interfaces";
import { formatBRL } from "../../utils/currency";
import { useEffect, useState } from "react";
import { useMutationPix } from "../../hooks/pagamento/mutationPix";
import { AxiosErrorResponse } from "../../hooks/pdv/mutationReserva";
import Swal from "sweetalert2";
import { MdPix } from "react-icons/md";
import { api } from "../../libs/axios";
import { queryClient } from "../../libs/queryClient";
import { PixTimerFooter } from '../../components/Modal/PixTimerFooter';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  fatura: Fatura;
  mode?: 'fatura' | 'parcial' | 'total_caixa';
  pixSelecionado?: Pix | null;
}


export function ModalPix({ isOpen, onClose, fatura, mode = 'fatura', pixSelecionado = null }: Props){
  const [pix, setPix] = useState<Pix | null>(null);

  const pixExibido = mode === 'total_caixa'
    ? (pix ?? fatura.pix_total_caixa)
    : mode === 'parcial'
      ? (pix ?? pixSelecionado)
      : pix;
  const valorPix = Number(pixExibido?.valor ?? (mode === 'total_caixa' ? fatura.pix_total_caixa?.valor : fatura.valor_total) ?? fatura.valor_total);
  const isPixTotalCaixa = mode === 'total_caixa';
  const isPixParcial = mode === 'parcial';
  const faturaIds = pix?.fatura_ids ?? fatura.pix_total_caixa?.fatura_ids ?? [];
  const tituloValor = isPixTotalCaixa
    ? 'VALOR TOTAL DO PIX CAIXA'
    : isPixParcial
      ? 'VALOR DO PIX PARCIAL'
      : 'VALOR TOTAL DA FATURA';
  const tituloModal = isPixTotalCaixa
    ? 'PAGAMENTO PIX TOTAL CAIXA - DETALHES DO PAGAMENTO'
    : isPixParcial
      ? 'PAGAMENTO PIX PARCIAL - DETALHES DO PAGAMENTO'
      : 'PAGAMENTO PIX - DETALHES DO PAGAMENTO';

  const mutation = useMutationPix(fatura.id, true);

  const fetchPixStatus = async (id: string) => {
    const response = await api.get(`/checkout/pix/consultar/fatura/${id}`);

    return response.data;
  }

  useEffect(() => {
    const gerarPix = async () => {
      try {
        if (mode === 'total_caixa') {
          setPix(fatura.pix_total_caixa ?? null);
          return;
        }

        if (mode === 'parcial') {
          setPix(pixSelecionado);
          return;
        }

        const pix = await mutation.mutateAsync()

        setPix(pix)
      }catch (err){
        const error = err as AxiosErrorResponse

        const requestId = error.response?.headers?.['request-id'];
        
        Swal.fire({
          title: "Ocorreu um erro",
          icon: "error",
          text: `${error.response?.data?.data?.message || "Erro desconhecido"} ${requestId ? `- Request ID: ${requestId}` : ''}`,
        })

        onClose()
        setPix(null)
      }
    }

    if(isOpen){
      setPix(null);
      gerarPix()
    } 
  }, [fatura.id, fatura.pix_total_caixa, isOpen, mode, pixSelecionado])

  useEffect(() => {
    let interval: ReturnType<typeof setTimeout>;

    const verificarPagamento = async () => {
      try {
        await fetchPixStatus(fatura.id);

          Swal.fire({
            icon: "success",
            title: "Pagamento confirmado!",
            text: "Pix recebido com sucesso",
          });

          clearInterval(interval);
          onClose();
          queryClient.invalidateQueries({
            queryKey: ['faturas'], exact: false,
          })
          window.location.href = `/fatura-finalizada/${fatura.id}`;
      }catch (err: unknown) {
        console.log(err);
      }
    };

    if (isOpen) {
      interval = setInterval(verificarPagamento, 10000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isOpen, fatura.id, onClose]);

  useEffect(() => {
    if (isOpen) {
      console.log('[PIX MODAL] aberto, estado pix:', pix);
    }
  }, [isOpen, pix]);

  return (
    <Modal
      size={"4xl"}
      key={"pagamento-dinheiro"}
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setPix(null);
      }}
    >
      <ModalOverlay />
      <ModalContent bg={'#f4f4f4'}>
        <ModalHeader>{tituloModal}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex
            mb={4}
            alignItems={"end"}
            justifyContent={"space-between"}
            w={"100%"}
          >
            <Text fontSize={"lg"}>{tituloValor}</Text>
            <Text fontSize={"3xl"} fontWeight={"bold"} textAlign={"right"}>
            {formatBRL(valorPix)}
            </Text>
          </Flex>
          {isPixTotalCaixa && faturaIds.length > 0 && (
            <Text fontSize="sm" color="gray.600" mb={4}>
              Este PIX quita as faturas #{faturaIds.join(', #')}.
            </Text>
          )}
          {isPixTotalCaixa && faturaIds.length === 0 && (
            <Text fontSize="sm" color="gray.600" mb={4}>
              Este PIX pode quitar mais de uma fatura em aberto da agência.
            </Text>
          )}
          <Flex gap={2} flexDir={'column'} alignItems={'center'} justifyContent={'center'}>
            {pixExibido?.qrCode ? (
              <Image 
                src={pixExibido.qrCode}
                alt="QR Code do pagamento PIX"
                key={pixExibido.qrCode}
              />
            ):(
              <Text fontSize="sm" color="red.500">
                QR Code não disponível no momento.
              </Text>
            )}
            <Text bg={'gray.200'} borderRadius={'md'} p={2} textAlign={'center'} maxW={"100%"} wordBreak={"break-all"} whiteSpace={"normal"} overflowWrap={"anywhere"}>{pixExibido?.dados_qrcode}</Text>
            <Button leftIcon={<MdPix />} onClick={async () => {
              await navigator.clipboard.writeText(pixExibido?.dados_qrcode ?? '')
              Swal.fire({
                icon:'success',
                title: 'Sucesso',
                text: 'O PIX foi copiado para área de transferência',
              })
            }}>Copiar PIX Copia e Cola</Button>
          </Flex>
        </ModalBody>
        <PixTimerFooter 
          isOpen={isOpen}
          dataExpiracao={pixExibido?.data_expiracao ?? ""}
        />
      </ModalContent>
    </Modal>
  );
}
